import { DOMSerializer, Fragment, Schema } from "prosemirror-model";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default class ClipboardDOMSerializer extends DOMSerializer {
	// Type definitions for DOMSerializer are incorrect; serializeFragment takes an additional
	// optional parameter internally, and can also return an Element rather than a DocumentFragment.
	// @ts-ignore
	serializeFragment(
		fragment: Fragment,
		options?: { [key: string]: any }, // eslint-disable-line @typescript-eslint/no-explicit-any
		target?: DocumentFragment,
	): DocumentFragment | Element {
		// @ts-ignore
		const serialized = super.serializeFragment(fragment, options, target) as DocumentFragment | Element;

		// If `target` is specified, this fragment was serialized into a contentDom node, so skip
		// our custom transformations.
		if (target) {
			return serialized;
		}

		// `result` holds the final serialized fragment.
		const doc = serialized.ownerDocument as Document;
		const result =
			serialized.nodeType == Node.DOCUMENT_FRAGMENT_NODE
				? doc.createDocumentFragment()
				: ((serialized as Element).cloneNode(false) as Element);
		let node: Element | DocumentFragment = result;

		let stack: Element[] = [];

		// Removes the top <ul> from the stack, and adds it to the end of the next one.
		const popStack = () => {
			const ul = stack.pop();
			const el = stack.length > 0 ? stack[stack.length - 1].lastChild : result;

			ul && el && el.appendChild(ul);
		};

		let el: ChildNode | null;

		while ((el = serialized.firstChild)) {
			// Most likely a text element - just add it unmodified to the result.
			if (el.nodeType != Node.ELEMENT_NODE) {
				node.appendChild(el);
				continue;
			}

			const indent = this.getIndent(el as Element);

			if (indent > stack.length) {
				// Indent is higher than our current level. Push <ul>s onto the stack until
				// it matches the indent level of this element.
				while (indent > stack.length) {
					const ul = (node = doc.createElement("ul"));
					const li = doc.createElement("li");

					li.setAttribute("data-fellow-serialized-list", "true");
					li.appendChild(el);
					ul.appendChild(li);

					stack.push(ul);
				}
			} else {
				// Indent is lower than our current level. Remove items from the stack until
				// it matches.
				while (stack.length > indent) {
					popStack();
				}

				// Add the current element either as an <li> or to the root fragment/element.
				node = stack.length > 0 ? stack[stack.length - 1] : result;

				if (indent > 0) {
					const li = doc.createElement("li");
					li.setAttribute("data-fellow-serialized-list", "true");
					li.appendChild(el);
					node.appendChild(li);
				} else {
					node.appendChild(el);
				}
			}
		}

		// Finalize by adding any items remaining on the stack to the result.
		while (stack.length > 0) {
			popStack();
		}

		return result;
	}

	getIndent(el: Element): number {
		const indent = el.getAttribute("data-indent");
		const blockType = el.getAttribute("data-block-type");

		return indent
			? Math.max(0, parseInt(indent)) + (blockType && ["todo", "agenda", "goal"].includes(blockType) ? 1 : 0)
			: 0;
	}

	static fromSchema<S extends Schema>(schema: S): DOMSerializer {
		const serializer = DOMSerializer.fromSchema(schema);

		return new this(serializer.nodes, serializer.marks) as DOMSerializer;
	}
}
